import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Util } from '@grandvisionhq/forms';
import { useAddressBookList, Actions, Utils } from '@grandvisionhq/my-account';
import { useIntl, useDispatch, useFeatureToggles, useSelector, statusUnavailable, statusUninit, statusLoading, statusWarning, statusFailed, statusSuccess } from '@grandvisionhq/state';
import { ShippingObjectTypes } from '../../../state.js';
import { togglePrefill, togglePrefillFailed } from '../../actions.js';
import { getShippingDetails, getDeliveryAddress, getAddressBookPrefillValidationResult } from '../../selectors.js';
import { PrefillSources } from '../../types.js';
import { AddressesSection } from './AddressesSection.js';
import { ValidatingForm } from './ValidatingForm/ValidatingForm.js';
import { useFormSlot, useBaseFormSlot } from '../../../slots/forms/form-context.js';

const AddressesSectionContainer = (props)=>{
    const { getLabel  } = useIntl();
    const dispatch = useDispatch();
    const { isAddressBookEnabled  } = useFeatureToggles();
    const addressBook = useAddressBookList({
        skip: !isAddressBookEnabled
    });
    const shippingDetails = useSelector(getShippingDetails);
    const deliveryAddressDetails = useSelector(getDeliveryAddress);
    const addressBookPrefillValidationResult = useSelector(getAddressBookPrefillValidationResult);
    const { billingAddress , deliveryAddress  } = useFormSlot();
    const editRef = React.useRef(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectionTarget1, setSelectionTarget] = React.useState(ShippingObjectTypes.BillingAddress);
    const addressOnCart = shippingDetails === null || shippingDetails === void 0 ? void 0 : shippingDetails[selectionTarget1];
    // TODO: type correctly Address
    // instead of using any, now casted to correct type
    const initialSelectedAddress = (addressOnCart === null || addressOnCart === void 0 ? void 0 : addressOnCart.addressBookId) ? swcHelpers.objectSpread({
        id: addressOnCart.addressBookId
    }, Util.getValuesFromState(addressOnCart.formData)) : undefined;
    const showBillingAddressSummary = !!shippingDetails && shippingDetails.billingAddress.isValid && addressBookPrefillValidationResult === 'success';
    const labels = React.useMemo(()=>({
            message: getLabel('checkout.addressBook.prefillFailed.message', {
                hideLabelKey: true
            }),
            titles: {
                addressSection: props.isDelivery && !deliveryAddressDetails ? getLabel('checkout.billingAndDeliveryAddress') : getLabel('checkout.billingAddress'),
                prefillFailed: getLabel('checkout.addressBook.prefillFailed.title')
            }
        })
    , [
        getLabel,
        props.isDelivery,
        deliveryAddressDetails
    ]);
    const isAddressBookUnavailable = addressBook && statusUnavailable(addressBook);
    const isAddressBookLoading = !addressBook || statusUninit(addressBook) || statusLoading(addressBook);
    const isAddressBookLoadingFailed = statusWarning(addressBook) || statusFailed(addressBook);
    const isAddressBookEmpty = statusSuccess(addressBook) && addressBook.result && addressBook.result.length < 1;
    const handleModalOpen = React.useCallback((selectionTarget)=>{
        setSelectionTarget(selectionTarget);
        setIsModalOpen(true);
    }, []);
    const handleModalClose = React.useCallback(()=>{
        setIsModalOpen(false);
        if (editRef === null || editRef === void 0 ? void 0 : editRef.current) {
            editRef.current.focus();
        }
    }, [
        setIsModalOpen,
        editRef
    ]);
    const handleAddAddress = React.useCallback((address)=>{
        dispatch(Actions.AddressBookActions.addAddress(swcHelpers.objectSpread({}, address, {
            addressTypes: []
        })));
    }, [
        dispatch
    ]);
    const onSelectAddress = React.useCallback((selectionTarget, address)=>{
        const targetFormState = shippingDetails ? shippingDetails[selectionTarget] : {
            isValid: false,
            isEnabled: false,
            formData: {}
        };
        dispatch(togglePrefill({
            source: PrefillSources.AddressBook,
            addressId: address.id,
            sourceFormName: selectionTarget,
            targetFormName: selectionTarget,
            targetFormState,
            data: {
                [selectionTarget]: address
            }
        }));
    }, [
        shippingDetails,
        dispatch
    ]);
    const handleSelectAddress = React.useCallback((addressContainer)=>{
        if ((addressOnCart === null || addressOnCart === void 0 ? void 0 : addressOnCart.addressBookId) !== addressContainer.id) {
            onSelectAddress(selectionTarget1, addressContainer);
        }
        handleModalClose();
    }, [
        addressOnCart,
        onSelectAddress,
        handleModalClose,
        selectionTarget1
    ]);
    React.useEffect(()=>{
        if (statusSuccess(addressBook)) {
            if (addressBookPrefillValidationResult === 'success') {
                return;
            }
            const addressList = addressBook.result;
            const prefillAddress = addressList.find((a)=>a.addressTypes.includes('billing')
            ) || addressList[0];
            if (prefillAddress) {
                const targetBillingAddressFormState = shippingDetails ? shippingDetails[ShippingObjectTypes.BillingAddress] : {
                    isValid: false,
                    isEnabled: false,
                    formData: {}
                };
                dispatch(togglePrefill({
                    source: PrefillSources.AddressBook,
                    addressId: prefillAddress.id,
                    targetFormState: targetBillingAddressFormState,
                    targetFormName: ShippingObjectTypes.BillingAddress,
                    sourceFormName: ShippingObjectTypes.BillingAddress,
                    data: {
                        [ShippingObjectTypes.BillingAddress]: Utils.AddressBookUtils.sanitizeAddress(prefillAddress)
                    }
                }));
            }
        }
        if (statusWarning(addressBook) || statusFailed(addressBook)) {
            dispatch(togglePrefillFailed({
                source: PrefillSources.AddressBook,
                reason: 'noAddressBook'
            }));
        }
    // This effect should only check for status of addressBook. Other deps results in multiple rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        addressBook.status
    ]);
    const { Slots: FormSlots  } = useBaseFormSlot();
    const addressForm = FormSlots.billingAddress ? /*#__PURE__*/ React.createElement(FormSlots.billingAddress, null) : undefined;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ValidatingForm, swcHelpers.extends({}, props, {
        billingAddressForm: billingAddress,
        addressBook: addressBook
    })), /*#__PURE__*/ React.createElement(AddressesSection, swcHelpers.extends({}, props, {
        billingAddressForm: billingAddress,
        addressForm: addressForm,
        deliveryAddressForm: deliveryAddress,
        editRef: editRef,
        handleModalClose: handleModalClose,
        handleModalOpen: handleModalOpen,
        handleSelectAddress: handleSelectAddress,
        initialSelectedAddress: initialSelectedAddress,
        isAddressBookEmpty: isAddressBookEmpty,
        isAddressBookLoading: isAddressBookLoading,
        isAddressBookLoadingFailed: isAddressBookLoadingFailed,
        isAddressBookUnavailable: isAddressBookUnavailable,
        isModalOpen: isModalOpen,
        labels: labels,
        onAddAddress: handleAddAddress,
        shippingDetails: shippingDetails,
        showBillingAddressSummary: showBillingAddressSummary
    })));
};

export { AddressesSectionContainer };
//# sourceMappingURL=AddressesSectionContainer.js.map
